/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useToast } from '../ui/ToastContext';
import { Notebook, NoteCard } from '../../types';
import { Trash2, RotateCcw, X, AlertCircle } from 'lucide-react';

interface RecycleBinModalProps {
  deletedNotebooks: Notebook[];
  deletedCards: NoteCard[];
  onRestoreNotebook: (id: string) => void;
  onRestoreCard: (id: string) => void;
  onDeleteNotebookPermanent: (id: string) => void;
  onDeleteCardPermanent: (id: string) => void;
  onEmptyBin: () => void;
  onClose: () => void;
}

export const RecycleBinModal: React.FC<RecycleBinModalProps> = ({
  deletedNotebooks,
  deletedCards,
  onRestoreNotebook,
  onRestoreCard,
  onDeleteNotebookPermanent,
  onDeleteCardPermanent,
  onEmptyBin,
  onClose,
}) => {
  const { confirm } = useToast();

  const hasItems = deletedNotebooks.length > 0 || deletedCards.length > 0;

  return (
    <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-xs flex items-center justify-center z-[110] p-4" id="recycle-bin-modal-container">
      <div className="bg-white dark:bg-slate-950 rounded-2xl max-w-xl w-full shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh] overflow-hidden transform duration-250 animate-in fade-in zoom-in-95">
        
        {/* Header Ribbon bar */}
        <div className="p-4 border-b border-slate-100 dark:border-slate-850 bg-slate-50 dark:bg-slate-900/50 flex justify-between items-center">
          <h3 className="font-sans font-bold text-lg text-slate-800 dark:text-white flex items-center gap-1.5">
            <Trash2 className="w-5 h-5 text-indigo-600" />
            Recycle Bin
          </h3>
          <div className="flex items-center gap-2">
            {hasItems && (
              <button
                onClick={async () => {
                  if (await confirm('Are you absolutely sure you want to permanently clear the Recycle Bin? This action is irreversible.')) {
                    onEmptyBin();
                  }
                }}
                className="text-xs bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-950/20 dark:text-rose-400 font-bold px-3 py-1.5 rounded-lg transition-colors border border-rose-100 dark:border-rose-905/20"
                id="btn-empty-recycle"
              >
                Empty Bin
              </button>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scroll list item content */}
        <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-6" id="recycle-bin-scrollable">
          
          {/* Deleted Notebooks segment */}
          {deletedNotebooks.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2 pb-1.5 border-b border-slate-150 dark:border-slate-850">
                Deleted Books ({deletedNotebooks.length})
              </h4>
              <div className="flex flex-col gap-2">
                {deletedNotebooks.map((nb) => (
                  <div
                    key={nb.id}
                    className="flex justify-between items-center bg-slate-50 dark:bg-slate-900/40 p-3 rounded-xl border border-slate-200 dark:border-slate-800/50"
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-4 h-6 rounded-r bg-indigo-600 shrink-0"
                        style={{ backgroundColor: nb.coverStyle.primaryColor }}
                      />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        {nb.title}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => onRestoreNotebook(nb.id)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 rounded-md transition-colors"
                        title="Restore Book"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (await confirm(`Do you want to permanently delete "${nb.title}"? Associated notes inside will be removed permanently too.`)) {
                            onDeleteNotebookPermanent(nb.id);
                          }
                        }}
                        className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-md transition-colors"
                        title="Delete permanently"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deleted Cards segment */}
          {deletedCards.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2 pb-1.5 border-b border-slate-150 dark:border-slate-850">
                Deleted Note Cards ({deletedCards.length})
              </h4>
              <div className="flex flex-col gap-2">
                {deletedCards.map((card) => (
                  <div
                    key={card.id}
                    className="flex justify-between items-center bg-slate-50 dark:bg-slate-900/40 p-3 rounded-xl border border-slate-200 dark:border-slate-800/50"
                  >
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded-sm bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-450 font-mono uppercase text-[9px] font-bold">
                          {card.type}
                        </span>
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate block">
                          {card.title || 'Untitled'}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5 font-sans">
                        Deleted at: {card.deletedAt ? new Date(card.deletedAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => onRestoreCard(card.id)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 rounded-md transition-colors"
                        title="Restore Note Card"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (await confirm(`Do you want to permanently delete "${card.title}"?`)) {
                            onDeleteCardPermanent(card.id);
                          }
                        }}
                        className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-md transition-colors"
                        title="Delete note permanently"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty fallback screen */}
          {!hasItems && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 bg-slate-100 dark:bg-slate-900 rounded-full flex items-center justify-center text-slate-400 mb-3">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h5 className="font-bold text-slate-700 dark:text-slate-350 text-sm">
                Recycle Bin is Empty
              </h5>
              <p className="text-xs text-slate-500 mt-1 max-w-[280px]">
                Notes and notebooks you delete will pile up here. You can retrieve them any time.
              </p>
            </div>
          )}

        </div>

        {/* Footer info banner */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-850 bg-slate-50 dark:bg-slate-900/50 text-[10px] text-slate-400 text-center font-semibold font-mono">
          Permanently deleted items cannot be restored.
        </div>

      </div>
    </div>
  );
};
